import { ArrowRight, ClipboardCheck, GitBranch } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type EvalSampleProjectionContext = {
	sourceLabel?: string;
	sampleId: string | null;
	message?: string | null;
	expectedIntent?: string | null;
	actualIntent?: string | null;
	expectedPb?: string | null;
	actualPb?: string | null;
	rootCauseHint?: string | null;
	responsibleAsset?: string | null;
	badcaseReason?: string | null;
	evalRunId?: string | null;
	sampleResultId?: string | null;
	badcaseId?: string | null;
	statusLabel?: string | null;
	severity?: string | null;
	rerunStatusLabel?: string | null;
	traceId?: string | null;
	adminRunId?: string | null;
};

export function buildEvalReplayHref(context: EvalSampleProjectionContext): string {
	const params = new URLSearchParams({
		mock: "1",
		source: "eval",
		sample_id: context.sampleId ?? "",
	});
	appendQueryParam(params, "message", context.message);
	appendQueryParam(params, "expected_intent", context.expectedIntent);
	appendQueryParam(params, "actual_intent", context.actualIntent);
	appendQueryParam(params, "expected_pb", context.expectedPb);
	appendQueryParam(params, "actual_pb", context.actualPb);
	appendQueryParam(params, "root_cause_hint", context.rootCauseHint);
	appendQueryParam(params, "responsible_asset", context.responsibleAsset);
	appendQueryParam(params, "badcase_reason", context.badcaseReason);
	appendQueryParam(params, "eval_run_id", context.evalRunId);
	appendQueryParam(params, "sample_result_id", context.sampleResultId);
	appendQueryParam(params, "badcase_id", context.badcaseId);
	return `/pipeline-testbench?${params.toString()}`;
}

export function EvalSampleProjection({
	context,
	actionHref,
	actionLabel,
	className,
}: {
	context: EvalSampleProjectionContext;
	actionHref?: string;
	actionLabel?: string;
	className?: string;
}) {
	const statusBadges = [
		context.sourceLabel,
		context.statusLabel,
		context.severity,
		context.rerunStatusLabel,
	].filter((item): item is string => Boolean(item));
	const comparisonRows = [
		{
			label: "意图判断",
			expected: context.expectedIntent ?? "未传入",
			actual: context.actualIntent ?? "未传入",
		},
		{
			label: "方案判断",
			expected: context.expectedPb ?? "无",
			actual: context.actualPb ?? "无",
		},
	];

	return (
		<section
			className={cn("rounded-md border border-surface-border bg-surface-background p-3", className)}
			data-testid="eval-sample-projection"
		>
			<div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
				<div className="min-w-0">
					<div className="flex items-center gap-2 text-text-primary">
						<span className="flex h-8 w-8 flex-none items-center justify-center rounded-md bg-surface-subtle text-brand-primary">
							<ClipboardCheck className="h-4 w-4" aria-hidden="true" />
						</span>
						<div>
							<h3 className="text-sm font-semibold">共同详情投影</h3>
							<p className="mt-1 text-xs leading-5 text-text-secondary">
								同一条样本在评测台账和链路复跑中使用一致口径。
							</p>
						</div>
					</div>
				</div>
				<div className="flex flex-wrap gap-1.5">
					{statusBadges.map((badge) => (
						<span
							key={badge}
							className="rounded border border-surface-border bg-surface-card px-2 py-1 text-xs font-medium text-text-secondary"
						>
							{badge}
						</span>
					))}
				</div>
			</div>

			{context.message ? (
				<p className="mt-3 rounded-md bg-surface-subtle px-3 py-2 text-sm leading-6 text-text-primary">
					{context.message}
				</p>
			) : null}

			<dl className="mt-3 grid gap-2 md:grid-cols-2">
				<ProjectionField label="样本编号" value={context.sampleId ?? "未关联"} />
				<ProjectionField label="样本结果" value={context.sampleResultId ?? "未关联"} />
				<ProjectionField label="Badcase" value={context.badcaseId ?? "未生成"} />
				<ProjectionField label="评测运行" value={context.evalRunId ?? "未关联"} />
				<ProjectionField label="根因归类" value={context.rootCauseHint ?? "待确认"} />
				<ProjectionField label="责任资产" value={context.responsibleAsset ?? "责任资产待确认"} />
				<ProjectionField label="复跑 Trace" value={context.traceId ?? "未写回"} />
				<ProjectionField label="复跑 Run" value={context.adminRunId ?? "未写回"} />
			</dl>

			<div className="mt-3 grid gap-2 md:grid-cols-2">
				{comparisonRows.map((row) => (
					<div
						key={row.label}
						className="rounded-md border border-surface-border bg-surface-card px-3 py-2"
					>
						<div className="flex items-center gap-2 text-xs font-medium text-text-muted">
							<GitBranch className="h-3.5 w-3.5" aria-hidden="true" />
							{row.label}
						</div>
						<div className="mt-2 grid gap-1 text-xs leading-5 text-text-secondary">
							<div>
								<span className="text-text-muted">期望：</span>
								<span className="font-mono text-text-primary">{row.expected}</span>
							</div>
							<div>
								<span className="text-text-muted">实际：</span>
								<span className="font-mono text-text-primary">{row.actual}</span>
							</div>
						</div>
					</div>
				))}
			</div>

			{context.badcaseReason ? (
				<div className="mt-3 rounded-md border border-brand-warning/30 bg-brand-warning/10 px-3 py-2 text-sm leading-6 text-brand-warning">
					{context.badcaseReason}
				</div>
			) : null}

			{actionHref && actionLabel ? (
				<div className="mt-3 flex justify-end">
					<Button asChild size="sm" variant="outline">
						<a href={actionHref}>
							{actionLabel}
							<ArrowRight className="ml-1.5 h-3.5 w-3.5" aria-hidden="true" />
						</a>
					</Button>
				</div>
			) : null}
		</section>
	);
}

function ProjectionField({ label, value }: { label: string; value: string }) {
	return (
		<div className="rounded-md border border-surface-border bg-surface-card px-3 py-2">
			<dt className="text-xs font-medium text-text-muted">{label}</dt>
			<dd className="mt-1 break-words font-mono text-xs text-text-primary">{value}</dd>
		</div>
	);
}

function appendQueryParam(params: URLSearchParams, key: string, value: string | null | undefined) {
	if (!value || value.trim().length === 0) return;
	params.set(key, value);
}
